import {
	Component,
	Input,
	Output,
	EventEmitter,
	OnInit,
	OnDestroy,
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	forwardRef,
} from "@angular/core";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";
import { Subject, takeUntil } from "rxjs";

// Models
import { TitularFangosViewModel } from "src/app/_core/models/centros/cebe-contract-list-base.model";

// Services
import { MasterService } from "src/app/_core/providers/master.service";
import { NotificationService } from "src/app/_core/providers/notification.service";

@Component({
    selector: "app-titular-fangos-autocomplete",
    templateUrl: "./titular-fangos-autocomplete.component.html",
    styleUrls: ["./titular-fangos-autocomplete.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => TitularFangosAutocompleteComponent),
            multi: true,
        },
    ],
    standalone: false
})
export class TitularFangosAutocompleteComponent
	implements OnInit, OnDestroy, ControlValueAccessor
{
	private readonly destroy$ = new Subject<void>();

	// Inputs
	@Input() label = "Titular de Fangos";
	@Input() placeholder = "Buscar titular de fangos por código o descripción";
	@Input() required = false;
	@Input() disabled = false;
	@Input() hint = "Busque por código o descripción de titular de fangos";

	// Outputs
	@Output() titularFangosSelected =
		new EventEmitter<TitularFangosViewModel>();

	// State
	titularesFangos: TitularFangosViewModel[] = [];
	titularFangosId: number | null = null;
	selectedTitularFangos: TitularFangosViewModel | null = null;

	// ControlValueAccessor
	private onChange = (_value: number | null): void => {
		// This is intentional
	};
	private onTouched = (): void => {
		// This is intentional
	};

	constructor(
		private readonly masterService: MasterService,
		private readonly notificationService: NotificationService,
		private readonly cdr: ChangeDetectorRef,
	) {}

	ngOnInit(): void {
		this.loadTitularesFangos();
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	// ControlValueAccessor implementation
	writeValue(value: number | null): void {
		this.titularFangosId = value ?? null;
		this.syncSelected();
		this.cdr.markForCheck();
	}

	registerOnChange(fn: (value: number | null) => void): void {
		this.onChange = fn;
	}

	registerOnTouched(fn: () => void): void {
		this.onTouched = fn;
	}

	setDisabledState(isDisabled: boolean): void {
		this.disabled = isDisabled;
		this.cdr.markForCheck();
	}

	/**
	 * Carga la lista de titulares de fangos
	 */
	private loadTitularesFangos(): void {
		this.masterService
			.getTitularFangosList()
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: (titularesFangos) => {
					this.titularesFangos = titularesFangos;
					this.syncSelected();
					this.cdr.markForCheck();
				},
				error: () => {
					this.notificationService.showError(
						"Error al cargar los titulares de fangos",
					);
				},
			});
	}

	/** Recalcula el objeto seleccionado a partir del id actual. */
	private syncSelected(): void {
		this.selectedTitularFangos =
			this.titularFangosId != null
				? (this.titularesFangos.find(
						(t) => t.id === this.titularFangosId,
					) ?? null)
				: null;
	}

	/**
	 * Obtiene el texto mostrado para un titular de fangos
	 */
	displayTitularFangos = (
		titularFangos: TitularFangosViewModel | string,
	): string => {
		if (typeof titularFangos === "string") {
			return titularFangos;
		}
		return titularFangos &&
			titularFangos.codigo &&
			titularFangos.descripcion
			? `${titularFangos.codigo} - ${titularFangos.descripcion}`
			: "";
	};

	/** Selección desde el autocomplete `.ds`. */
	onValueChange(titularFangos: TitularFangosViewModel | null): void {
		this.titularFangosId = titularFangos ? titularFangos.id : null;
		this.selectedTitularFangos = titularFangos;
		this.onChange(this.titularFangosId);
		this.onTouched();
		if (titularFangos) {
			this.titularFangosSelected.emit(titularFangos);
		}
		this.cdr.markForCheck();
	}

	onControlTouched(): void {
		this.onTouched();
	}
}
